import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Select from "react-select";
import { axiosInstance } from "../../utils/networking";
import { UserType } from "../../interfaces";

// Offboard a departing staff member: reassign their open cases to a chosen
// successor, then deactivate the account. Mirrors the ReassignClient modal
// pattern (react-select successor picker, branded header).
const OffboardUser = (props: any) => {
  const departing = props.user;

  const [preview, setPreview] = useState<{
    primaryOpen: number;
    secondary: number;
  } | null>(null);
  const [successorOptions, setSuccessorOptions] = useState<
    { value: any; label: string }[]
  >([]);
  const [selectedSuccessor, setSelectedSuccessor] = useState<{
    value: any;
    label: string;
  } | null>(null);
  const [reassignSecondary, setReassignSecondary] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance
      .get(`/user/offboard/preview`, { params: { userId: departing._id } })
      .then((res) => {
        setPreview({
          primaryOpen: res.data.primaryOpen,
          secondary: res.data.secondary,
        });
      })
      .catch(() => setError("Could not load this user's workload."));

    axiosInstance.get(`/user/allUsers`).then((res) => {
      const users = res.data.users as UserType[];
      const options = users
        .filter(
          (u: any) =>
            u._id !== departing._id &&
            u.role !== "deleted" &&
            u.status !== "inactive"
        )
        .map((u: any) => ({
          value: u._id,
          label: `${u.firstName} ${u.lastName}`,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));
      setSuccessorOptions(options);
    });
  }, [departing._id]);

  const handleSubmit = () => {
    if (!selectedSuccessor) {
      setError("Select a successor to receive the reassigned cases.");
      return;
    }
    setSubmitting(true);
    setError("");
    axiosInstance
      .post(`/user/offboard`, {
        departingUserId: departing._id,
        successorUserId: selectedSuccessor.value,
        reassignSecondary,
        webIntakeStrategy: "reassign",
      })
      .then((res) => {
        const { casesReassigned, secondaryReassigned, webIntakesReassigned } =
          res.data;
        alert(
          `Offboarded ${departing.firstName} ${departing.lastName}.\n` +
            `Reassigned ${casesReassigned} case(s), ` +
            `${secondaryReassigned} secondary assignment(s), ` +
            `${webIntakesReassigned} web intake(s).`
        );
        props.setOpen(false);
        window.location.reload();
      })
      .catch((err) => {
        setSubmitting(false);
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Offboarding failed. No changes were applied to the account."
        );
      });
  };

  return (
    <>
      <Box
        sx={{
          textAlign: "center",
          backgroundColor: "#9E1F63FF",
          padding: "3px",
          margin: 0,
          color: "white",
          borderRadius: "5px 5px 0 0",
        }}
      >
        <Typography sx={{ display: "inline-block" }} variant="h4">
          OFFBOARD USER
        </Typography>
        <Box
          sx={{ display: "inline", float: "right", cursor: "pointer" }}
          onClick={() => props.setOpen(false)}
        >
          <CloseIcon sx={{ fontSize: 40 }}></CloseIcon>
        </Box>
      </Box>

      <Stack sx={{ padding: "24px" }} spacing={2}>
        <Typography variant="h6">
          {departing.firstName} {departing.lastName}{" "}
          <span style={{ color: "#666" }}>({departing.email})</span>
        </Typography>

        {preview === null && !error ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography>Loading workload…</Typography>
          </Stack>
        ) : preview ? (
          <Typography>
            This will move <b>{preview.primaryOpen}</b> open case(s) where this
            user is the primary BFC
            {reassignSecondary ? (
              <>
                {" "}
                and <b>{preview.secondary}</b> case(s) where they are the
                secondary BFC
              </>
            ) : null}{" "}
            to the selected successor, then deactivate the account.
          </Typography>
        ) : null}

        <Box>
          <Typography sx={{ mb: 1 }}>Reassign all work to:</Typography>
          <Select
            value={selectedSuccessor}
            onChange={(v: any) => setSelectedSuccessor(v)}
            placeholder="Select a successor"
            options={successorOptions}
            styles={{
              control: (provided) => ({ ...provided, height: "50px" }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? "#9D2362" : "white",
                color: state.isSelected ? "white" : "black",
                ":hover": {
                  backgroundColor: state.isSelected ? "#9D2362" : "#F3F4F6",
                },
              }),
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={reassignSecondary}
              onChange={(e) => setReassignSecondary(e.target.checked)}
            />
          }
          label="Also reassign secondary-BFC assignments"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
          }
          label="I understand this deactivates the user and cannot be undone from this screen."
        />

        {error ? <Typography color="error">{error}</Typography> : null}

        <Button
          variant="contained"
          disabled={!confirmed || !selectedSuccessor || submitting}
          sx={{ backgroundColor: "#9E1F63FF" }}
          onClick={handleSubmit}
        >
          {submitting ? "Offboarding…" : "Offboard & Reassign"}
        </Button>
      </Stack>
    </>
  );
};

export default OffboardUser;
